import * as THREE from 'three'
import type { Location } from '../types'

const RAY_COUNT_CLIPPING = 64
const RAY_COUNT_PROJECTION = 512
const MAX_RADIUS = 20
const DEG2RAD = Math.PI / 180

export class ZoneRenderer {
  group: THREE.Group
  coneMesh: THREE.Mesh
  zoneMesh: THREE.Mesh | null = null
  projectionMesh: THREE.Mesh | null = null
  markerGroup: THREE.Group | null = null
  heightLine: THREE.Line | null = null
  data: Location
  private raycaster = new THREE.Raycaster()
  isSelected = false

  constructor(location: Location) {
    this.data = location
    this.group = new THREE.Group()
    this.group.name = `zone-${location.id}`
    this.group.userData = { zoneId: location.id, type: 'zone', name: location.name, zoneType: location.type }

    // Position in floor-local coords
    this.group.position.set(location.x, 0, location.z)

    // Create cone
    this.coneMesh = this.createCone()
    this.group.add(this.coneMesh)

    // Create marker
    this.createMarker()

    // Create clipped zone from pre-computed points if available
    if (location.clipped_points && location.clipped_points.length > 0) {
      this.zoneMesh = this.createZoneMeshFromPoints(location.clipped_points)
      this.group.add(this.zoneMesh)
    }
  }

  private createCone(): THREE.Mesh {
    const halfAngle = (this.data.cone_angle / 2) * DEG2RAD
    const height = this.data.height
    const radius = height * Math.tan(halfAngle)

    const geometry = new THREE.ConeGeometry(radius, height, 32, 1, true)
    // Cone points down: rotate 180 degrees, then shift up by height
    geometry.translate(0, -height / 2, 0)

    const color = this.data.color || '#00aaff'
    const material = new THREE.MeshPhongMaterial({
      color,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.y = height
    mesh.name = `cone-${this.data.id}`

    // Apply orientation: azimuth (Y rotation) + tilt (X rotation)
    const quaternion = new THREE.Quaternion()
    const azimuthQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      this.data.azimuth_angle * DEG2RAD
    )
    const tiltQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0),
      this.data.tilt_angle * DEG2RAD
    )
    quaternion.multiplyQuaternions(azimuthQ, tiltQ)
    mesh.quaternion.copy(quaternion)

    return mesh
  }

  private createMarker() {
    // Remove existing marker and height line
    if (this.markerGroup) {
      this.group.remove(this.markerGroup)
      this.markerGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose()
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m) => m.dispose())
          } else {
            obj.material.dispose()
          }
        }
      })
    }
    if (this.heightLine) {
      this.group.remove(this.heightLine)
      this.heightLine.geometry.dispose()
      ;(this.heightLine.material as THREE.Material).dispose()
      this.heightLine = null
    }

    this.markerGroup = new THREE.Group()
    this.markerGroup.name = `marker-${this.data.id}`
    this.markerGroup.position.y = this.data.height

    const color = this.data.color || '#00aaff'
    const zoneType = this.data.type

    if (zoneType === 'FLEXIBLE_BUREAU') {
      this.createDeskLampMarker(color)
    } else if (zoneType === 'BORDER_ROUTER') {
      this.createCubeMarker(color)
    } else {
      this.createDefaultMarker(color)
    }

    this.group.add(this.markerGroup)

    // Height indicator (vertical line from marker to ground)
    if (zoneType !== 'BORDER_ROUTER') {
      this.createHeightIndicator(color)
    }
  }

  private createDefaultMarker(color: string) {
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.9,
    })

    // Base cylinder
    const baseGeo = new THREE.CylinderGeometry(0.1, 0.15, 0.3, 8)
    const baseMesh = new THREE.Mesh(baseGeo, mat)
    baseMesh.position.y = 0.15
    this.markerGroup!.add(baseMesh)

    // Top sphere
    const sphereGeo = new THREE.SphereGeometry(0.125, 16, 16)
    const sphereMesh = new THREE.Mesh(sphereGeo, mat.clone())
    sphereMesh.position.y = 0.35
    this.markerGroup!.add(sphereMesh)
  }

  private createDeskLampMarker(color: string) {
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.9,
    })

    // Base
    const baseGeo = new THREE.CylinderGeometry(0.0375, 0.045, 0.0075, 16)
    const baseMesh = new THREE.Mesh(baseGeo, mat)
    baseMesh.position.y = 0.00375
    this.markerGroup!.add(baseMesh)

    // Pole
    const poleGeo = new THREE.CylinderGeometry(0.005, 0.005, 0.0875, 8)
    const poleMesh = new THREE.Mesh(poleGeo, mat.clone())
    poleMesh.position.y = 0.05125
    this.markerGroup!.add(poleMesh)

    // Arm
    const armGeo = new THREE.CylinderGeometry(0.00375, 0.00375, 0.075, 8)
    const armMesh = new THREE.Mesh(armGeo, mat.clone())
    armMesh.position.set(0.02, 0.12, 0)
    armMesh.rotation.z = -Math.PI / 4
    this.markerGroup!.add(armMesh)

    // Head
    const headGeo = new THREE.ConeGeometry(0.025, 0.03, 16)
    const headMesh = new THREE.Mesh(headGeo, mat.clone())
    headMesh.position.set(0.045, 0.145, 0)
    headMesh.rotation.z = Math.PI / 2 + Math.PI / 6
    this.markerGroup!.add(headMesh)

    // Bulb
    const bulbMat = new THREE.MeshStandardMaterial({
      color: 0xffffcc,
      emissive: 0xffffcc,
      emissiveIntensity: 0.8,
      transparent: true,
      opacity: 0.9,
    })
    const bulbGeo = new THREE.SphereGeometry(0.01, 8, 8)
    const bulbMesh = new THREE.Mesh(bulbGeo, bulbMat)
    bulbMesh.position.set(0.055, 0.135, 0)
    this.markerGroup!.add(bulbMesh)
  }

  private createCubeMarker(color: string) {
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.9,
    })
    const boxGeo = new THREE.BoxGeometry(0.2, 0.2, 0.2)
    const boxMesh = new THREE.Mesh(boxGeo, mat)
    boxMesh.position.y = 0.1
    this.markerGroup!.add(boxMesh)
  }

  private createHeightIndicator(color: string) {
    const lineMat = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0.4,
    })
    const points = [
      new THREE.Vector3(0, this.data.height, 0),
      new THREE.Vector3(0, 0, 0),
    ]
    const lineGeo = new THREE.BufferGeometry().setFromPoints(points)
    this.heightLine = new THREE.Line(lineGeo, lineMat)
    this.heightLine.name = `height-line-${this.data.id}`
    this.group.add(this.heightLine)
  }

  private createZoneMeshFromPoints(points: { x: number; z: number }[]): THREE.Mesh {
    if (points.length < 3) {
      return new THREE.Mesh(new THREE.BufferGeometry())
    }

    const shape = new THREE.Shape()
    shape.moveTo(points[0]!.x, points[0]!.z)
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i]!.x, points[i]!.z)
    }
    shape.closePath()

    const geometry = new THREE.ShapeGeometry(shape)
    // Rotate to lie on XZ plane (ShapeGeometry is on XY)
    geometry.rotateX(-Math.PI / 2)
    geometry.translate(0, 0.02, 0) // Slight offset above floor

    const color = this.data.color || '#00aaff'
    const material = new THREE.MeshPhongMaterial({
      color,
      transparent: true,
      opacity: 0.25,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = `zone-ground-${this.data.id}`
    // Zone points are relative to zone position, already centered
    return mesh
  }

  computeClippedZone(wallMeshes: THREE.Mesh[]) {
    const points: { x: number; z: number }[] = []
    const halfAngle = (this.data.cone_angle / 2) * DEG2RAD
    const maxR = this.data.height * Math.tan(halfAngle)

    // Get world position of zone center for raycasting
    const worldPos = new THREE.Vector3()
    this.group.getWorldPosition(worldPos)

    for (let i = 0; i < RAY_COUNT_CLIPPING; i++) {
      const angle = (i / RAY_COUNT_CLIPPING) * Math.PI * 2
      const dir = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))

      this.raycaster.set(
        new THREE.Vector3(worldPos.x, worldPos.y + 0.1, worldPos.z),
        dir
      )
      this.raycaster.far = Math.min(maxR, MAX_RADIUS)

      const intersections = this.raycaster.intersectObjects(wallMeshes, true)
      let dist = Math.min(maxR, MAX_RADIUS)
      if (intersections.length > 0) {
        dist = Math.min(dist, intersections[0]!.distance)
      }

      points.push({
        x: dir.x * dist,
        z: dir.z * dist,
      })
    }

    // Replace zone mesh
    if (this.zoneMesh) {
      this.group.remove(this.zoneMesh)
      this.zoneMesh.geometry.dispose()
      ;(this.zoneMesh.material as THREE.Material).dispose()
    }

    if (points.length >= 3) {
      this.zoneMesh = this.createZoneMeshFromPoints(points)
      this.group.add(this.zoneMesh)
    }
  }

  computeConeProjection(wallMeshes: THREE.Mesh[]) {
    if (wallMeshes.length === 0) return

    const halfAngle = (this.data.cone_angle / 2) * DEG2RAD
    // Use world position for raycasting
    const worldPos = new THREE.Vector3()
    this.group.getWorldPosition(worldPos)
    const origin = new THREE.Vector3(worldPos.x, worldPos.y + this.data.height, worldPos.z)

    // Cone axis (points down by default)
    const axis = new THREE.Vector3(0, -1, 0)
    // Apply azimuth + tilt
    const orientQ = new THREE.Quaternion()
    const azimuthQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      this.data.azimuth_angle * DEG2RAD
    )
    const tiltQ = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0),
      this.data.tilt_angle * DEG2RAD
    )
    orientQ.multiplyQuaternions(azimuthQ, tiltQ)
    axis.applyQuaternion(orientQ)

    // Orthonormal basis around axis
    const up = Math.abs(axis.y) < 0.99
      ? new THREE.Vector3(0, 1, 0)
      : new THREE.Vector3(1, 0, 0)
    const base1 = new THREE.Vector3().crossVectors(axis, up).normalize()
    const base2 = new THREE.Vector3().crossVectors(axis, base1).normalize()

    const projPoints: THREE.Vector3[] = []

    for (let i = 0; i < RAY_COUNT_PROJECTION; i++) {
      const a = (i / RAY_COUNT_PROJECTION) * Math.PI * 2
      const dir = new THREE.Vector3()
        .copy(axis)
        .multiplyScalar(Math.cos(halfAngle))
        .addScaledVector(base1, Math.sin(halfAngle) * Math.cos(a))
        .addScaledVector(base2, Math.sin(halfAngle) * Math.sin(a))
        .normalize()

      this.raycaster.set(origin, dir)
      this.raycaster.far = MAX_RADIUS

      const hits = this.raycaster.intersectObjects(wallMeshes, true)
      if (hits.length > 0) {
        projPoints.push(hits[0]!.point.clone())
      }
    }

    // Replace projection mesh
    if (this.projectionMesh) {
      this.group.remove(this.projectionMesh)
      this.projectionMesh.geometry.dispose()
      ;(this.projectionMesh.material as THREE.Material).dispose()
      this.projectionMesh = null
    }

    if (projPoints.length >= 3) {
      this.projectionMesh = this.createProjectionMesh(projPoints, origin)
      this.group.add(this.projectionMesh)
    }
  }

  private createProjectionMesh(points: THREE.Vector3[], _origin: THREE.Vector3): THREE.Mesh {
    // Convert world hit points to local group coordinates
    const worldPos = new THREE.Vector3()
    this.group.getWorldPosition(worldPos)

    const positions = new Float32Array(points.length * 3)
    for (let i = 0; i < points.length; i++) {
      positions[i * 3] = points[i]!.x - worldPos.x
      positions[i * 3 + 1] = points[i]!.y - worldPos.y
      positions[i * 3 + 2] = points[i]!.z - worldPos.z
    }

    // Simple triangle fan from centroid
    const cx = positions.reduce((s, _, i) => i % 3 === 0 ? s + positions[i]! : s, 0) / points.length
    const cy = positions.reduce((s, _, i) => i % 3 === 1 ? s + positions[i]! : s, 0) / points.length
    const cz = positions.reduce((s, _, i) => i % 3 === 2 ? s + positions[i]! : s, 0) / points.length

    const verts: number[] = []
    for (let i = 0; i < points.length; i++) {
      const next = (i + 1) % points.length
      verts.push(cx, cy, cz)
      verts.push(positions[i * 3]!, positions[i * 3 + 1]!, positions[i * 3 + 2]!)
      verts.push(positions[next * 3]!, positions[next * 3 + 1]!, positions[next * 3 + 2]!)
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3))
    geometry.computeVertexNormals()

    const color = this.data.color || '#00aaff'
    const material = new THREE.MeshPhongMaterial({
      color,
      transparent: true,
      opacity: 0.2,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = `zone-projection-${this.data.id}`
    return mesh
  }

  update(data: Partial<Location>) {
    Object.assign(this.data, data)
    this.group.position.set(this.data.x, 0, this.data.z)

    // Rebuild cone with new dimensions/orientation
    this.group.remove(this.coneMesh)
    this.coneMesh.geometry.dispose()
    ;(this.coneMesh.material as THREE.Material).dispose()
    this.coneMesh = this.createCone()
    this.group.add(this.coneMesh)

    // Rebuild marker
    this.createMarker()

    // Rebuild zone from updated clipped_points
    if (data.clipped_points) {
      if (this.zoneMesh) {
        this.group.remove(this.zoneMesh)
        this.zoneMesh.geometry.dispose()
        ;(this.zoneMesh.material as THREE.Material).dispose()
      }
      this.zoneMesh = this.createZoneMeshFromPoints(data.clipped_points)
      this.group.add(this.zoneMesh)
    }
  }

  setSelected(selected: boolean) {
    this.isSelected = selected
    const mat = this.coneMesh.material as THREE.MeshPhongMaterial
    if (selected) {
      mat.emissive.setHex(0x444444)
      mat.opacity = 0.5
    } else {
      mat.emissive.setHex(0x000000)
      mat.opacity = 0.3
    }

    // Update marker emissive intensity
    if (this.markerGroup) {
      this.markerGroup.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          const markerMat = obj.material as THREE.MeshStandardMaterial
          if (markerMat.emissiveIntensity !== undefined) {
            markerMat.emissiveIntensity = selected ? 0.6 : 0.3
          }
        }
      })
    }
  }

  setVisible(visible: boolean) {
    this.group.visible = visible
  }

  dispose() {
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
        obj.geometry.dispose()
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose())
        } else {
          obj.material.dispose()
        }
      }
    })
  }
}
